'use client';

import React, { useState, useEffect, useRef } from 'react';
import {
  Activity, Pause, Play, Square, RotateCcw, Wifi, WifiOff, Settings, Download,
  ChevronDown, Clock, User, Hash, LogOut, Shield, Stethoscope, Heart, Brain,
  Layers, Target, MessageSquare, Watch, FlaskConical, Bell, Dumbbell, Users,
  LayoutGrid, ChevronRight, Home, BarChart2, Database, Menu, X, Zap, Dna, BookOpen, Cpu, ShieldCheck
} from 'lucide-react';
import { useSession } from './SessionProvider';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter, usePathname } from 'next/navigation';
import CognitiveAlertBell from './CognitiveAlertBell';

function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

interface NavGroup {
  label: string;
  items: {
    href: string;
    label: string;
    icon: React.ReactNode;
    roles?: string[];
    accent?: string;
    scrollTo?: string;
  }[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Dashboards',
    items: [
      { href: '/eeg-cognitive-dashboard', label: 'EEG Dashboard', icon: <Brain size={14} /> },
      { href: '/cognitive-os', label: 'Brain Intelligence', icon: <Cpu size={14} />, accent: 'text-orange-400', roles: ['clinician', 'admin', 'researcher'] },
      { href: '/patient-dashboard', label: 'Patient Dashboard', icon: <Home size={14} />, roles: ['patient'] },
      { href: '/clinician-portal', label: 'Clinician Portal', icon: <Stethoscope size={14} />, roles: ['clinician', 'admin'] },
      { href: '/admin-dashboard', label: 'Admin Dashboard', icon: <Shield size={14} />, roles: ['admin'] },
      { href: '/researcher-portal', label: 'Researcher Portal', icon: <Database size={14} />, roles: ['researcher', 'admin'] },
      { href: '/care-team-dashboard', label: 'Care Team', icon: <Users size={14} />, roles: ['clinician', 'admin'] },
    ],
  },
  {
    label: 'Clinical',
    items: [
      { href: '/patient-progress', label: 'Patient Progress', icon: <Target size={14} />, roles: ['clinician', 'admin', 'researcher'] },
      { href: '/biomarker-alerts', label: 'Biomarker Alerts', icon: <Bell size={14} />, roles: ['clinician', 'admin'], accent: 'text-red-400' },
      { href: '/intervention-protocols', label: 'Protocols', icon: <FlaskConical size={14} />, roles: ['clinician', 'admin'] },
      { href: '/case-discussion', label: 'Case Discussions', icon: <MessageSquare size={14} />, roles: ['clinician', 'admin'] },
    ],
  },
  {
    label: 'Data & Research',
    items: [
      { href: '/neuro-data-hub', label: 'Neuro Data Hub', icon: <Brain size={14} />, roles: ['clinician', 'admin', 'researcher'] },
      { href: '/cohort-analytics', label: 'Cohort Analytics', icon: <BarChart2 size={14} />, roles: ['admin', 'researcher'] },
      { href: '/cohort-management', label: 'Cohort Management', icon: <Layers size={14} />, roles: ['admin', 'researcher'] },
      { href: '/wearable-integration', label: 'Wearables', icon: <Watch size={14} /> },
      { href: '/eeg-diagnostic-atlas', label: 'EEG Diagnostic Atlas', icon: <BookOpen size={14} />, roles: ['clinician', 'admin', 'researcher'], accent: 'text-cyan-400' },
    ],
  },
  {
    label: 'My Tools',
    items: [
      { href: '/brain-check', label: 'Brain Check', icon: <Zap size={14} />, accent: 'text-orange-400' },
      { href: '/brain-gym', label: 'Brain Gym', icon: <Dumbbell size={14} />, accent: 'text-violet-400' },
      { href: '/biological-age', label: 'Biological Age', icon: <Dna size={14} />, accent: 'text-rose-400' },
    ],
  },
  {
    label: 'Settings & Admin',
    items: [
      { href: '/validation-dashboard', label: 'Validation', icon: <ShieldCheck size={14} />, roles: ['admin', 'clinician'], accent: 'text-sky-400' },
      { href: '/api-validation-report', label: 'API Connections', icon: <ShieldCheck size={14} />, roles: ['admin'], accent: 'text-violet-400' },
    ],
  },
];

export default function DashboardTopbar() {
  const { status, sessionDuration, sampleRate, subjectId, sessionId, togglePause, stopSession, restartSession } = useSession();
  const [currentTime, setCurrentTime] = useState('');
  const [showNavMenu, setShowNavMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSessionInfo, setShowSessionInfo] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const { profile, role, signOut } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const navRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);
  const sessionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const update = () => setCurrentTime(new Date().toLocaleTimeString('en-US', { hour12: false }));
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setShowNavMenu(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) setShowUserMenu(false);
      if (sessionRef.current && !sessionRef.current.contains(e.target as Node)) setShowSessionInfo(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setShowMobileMenu(false);
  }, [pathname]);

  const handleStop = () => {
    stopSession();
    toast.error('Session stopped', { description: `${sessionId} ended after ${formatDuration(sessionDuration)}` });
  };

  const handleExport = () => {
    toast.success('Export queued', { description: 'Session data will be exported as EDF+ format' });
  };

  const handleSignOut = async () => {
    try { await signOut(); } catch { /* noop */ }
    window.location.href = '/login';
  };

  const roleDashboardHref = role === 'admin' ? '/admin-dashboard' : role === 'clinician' ? '/clinician-portal' : role === 'patient' ? '/patient-dashboard' : '/login';
  const roleLabel = role === 'admin' ? 'Admin' : role === 'clinician' ? 'Clinician' : role === 'patient' ? 'Patient' : null;
  const roleIcon = role === 'admin' ? <Shield size={11} /> : role === 'clinician' ? <Stethoscope size={11} /> : role === 'patient' ? <User size={11} /> : null;
  const roleBadgeClass = role === 'admin' ? 'text-red-400 bg-red-950/30 border-red-800/40' : role === 'clinician' ? 'text-sky-400 bg-sky-950/30 border-sky-800/40' : role === 'patient' ? 'text-emerald-400 bg-emerald-950/30 border-emerald-800/40' : 'text-zinc-400 bg-zinc-800/30 border-zinc-700/40';

  const statusConfig = {
    connecting: { label: 'Connecting', color: 'text-amber-400', dot: 'bg-amber-400', pulse: true },
    streaming: { label: 'Live', color: 'text-emerald-400', dot: 'bg-emerald-400', pulse: true },
    paused: { label: 'Paused', color: 'text-sky-400', dot: 'bg-sky-400', pulse: false },
    stopped: { label: 'Stopped', color: 'text-zinc-500', dot: 'bg-zinc-500', pulse: false },
  }[status];

  // Filter nav groups by role
  const filteredGroups = NAV_GROUPS.map(group => ({
    ...group,
    items: group.items.filter(item => !item.roles || item.roles.includes(role ?? '') || !role),
  })).filter(group => group.items.length > 0);

  return (
    <>
      <header className="app-header" role="banner">
        <div className="accent-line" aria-hidden="true" />

        <div className="app-header-inner">
          {/* ── LEFT ZONE: Logo + Navigate ── */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Logo */}
            <Link href={roleDashboardHref} className="flex items-center gap-2 group" aria-label="Go to dashboard">
              <AppLogo size={26} />
              <span className="font-bold text-[14px] tracking-tight hidden sm:block">
                <span className="text-[#F97316]">iBrain</span>
                <span className="text-white"> LAB</span>
              </span>
            </Link>

            <div className="w-px h-5 bg-[#1E3A5F] hidden sm:block" aria-hidden="true" />

            {/* Navigate dropdown — desktop only */}
            <div ref={navRef} className="relative hidden sm:block">
              <button
                onClick={() => setShowNavMenu(v => !v)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-[12px] font-medium transition-all duration-150 touch-manipulation"
                style={{
                  background: showNavMenu ? 'rgba(249,115,22,0.08)' : 'rgba(11,22,40,0.8)',
                  borderColor: showNavMenu ? 'rgba(249,115,22,0.4)' : 'rgba(30,58,95,0.5)',
                  color: showNavMenu ? '#F97316' : '#8BAFD4',
                  minHeight: '36px',
                }}
                aria-label="Navigation menu"
                aria-expanded={showNavMenu}
                aria-haspopup="true"
              >
                <LayoutGrid size={13} aria-hidden="true" />
                <span>Navigate</span>
                <ChevronDown size={11} className={`transition-transform duration-200 ${showNavMenu ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>

              {showNavMenu && (
                <div
                  className="absolute top-full left-0 mt-2 w-[520px] rounded-2xl border z-50 p-3 animate-scale-in"
                  style={{
                    background: 'rgba(8,16,30,0.98)',
                    borderColor: 'rgba(30,58,95,0.8)',
                    boxShadow: '0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px rgba(249,115,22,0.08)',
                    backdropFilter: 'blur(20px)',
                  }}
                  role="dialog"
                  aria-label="Navigation"
                >
                  <div className="grid grid-cols-2 gap-1">
                    {filteredGroups.map(group => (
                      <div key={group.label} className="space-y-0.5">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 px-2 py-1.5">{group.label}</p>
                        {group.items.map(item => {
                          const isActive = pathname === item.href;
                          return (
                            <Link
                              key={item.href}
                              href={item.href}
                              onClick={() => setShowNavMenu(false)}
                              className={`flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-[12px] font-medium transition-all duration-150 group touch-manipulation ${
                                isActive
                                  ? 'bg-[#F97316]/10 text-[#F97316] border border-[#F97316]/20'
                                  : 'text-zinc-400 hover:text-white hover:bg-[#0F1E35] border border-transparent'
                              }`}
                              style={{ minHeight: '36px' }}
                            >
                              <span className={`flex-shrink-0 ${item.accent ?? (isActive ? 'text-[#F97316]' : 'text-[#2D5A8E] group-hover:text-zinc-300')}`} aria-hidden="true">
                                {item.icon}
                              </span>
                              <span className="flex-1">{item.label}</span>
                              {isActive && <span className="w-1.5 h-1.5 rounded-full bg-[#F97316] flex-shrink-0" aria-hidden="true" />}
                            </Link>
                          );
                        })}
                      </div>
                    ))}
                  </div>

                  {/* PPG scroll anchor */}
                  {role !== 'patient' && (
                    <>
                      <div className="border-t border-[#1E3A5F]/30 mt-2 pt-2">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 px-2 py-1.5">Quick Access</p>
                        <button
                          onClick={() => {
                            setShowNavMenu(false);
                            const el = document.querySelector('[data-section="ppg"]');
                            el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }}
                          className="flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-[12px] font-medium text-zinc-400 hover:text-rose-400 hover:bg-rose-950/20 border border-transparent transition-all duration-150 touch-manipulation w-full text-left"
                          style={{ minHeight: '36px' }}
                        >
                          <Heart size={14} className="text-[#2D5A8E]" aria-hidden="true" />
                          PPG Heart Rate Monitor
                          <ChevronRight size={11} className="ml-auto text-zinc-600" aria-hidden="true" />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── MIDDLE ZONE: Session info (desktop) ── */}
          <div ref={sessionRef} className="relative hidden lg:block flex-shrink-0">
            <button
              onClick={() => setShowSessionInfo(v => !v)}
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-[11px] font-medium transition-all duration-150 touch-manipulation"
              style={{
                background: 'rgba(11,22,40,0.8)',
                borderColor: 'rgba(30,58,95,0.5)',
                color: '#8BAFD4',
                minHeight: '36px',
              }}
              aria-label="Session details"
              aria-expanded={showSessionInfo}
            >
              <Hash size={11} className="text-[#2D5A8E]" aria-hidden="true" />
              <span className="font-mono text-zinc-500 max-w-[100px] truncate">{sessionId}</span>
              <span className="text-zinc-600">·</span>
              <User size={11} className="text-[#2D5A8E]" aria-hidden="true" />
              <span className="text-zinc-400">{subjectId}</span>
              <ChevronDown size={10} className={`text-zinc-600 transition-transform duration-200 ${showSessionInfo ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>

            {showSessionInfo && (
              <div
                className="absolute top-full left-0 mt-2 w-64 rounded-xl border z-50 p-3 animate-scale-in"
                style={{
                  background: 'rgba(8,16,30,0.98)',
                  borderColor: 'rgba(30,58,95,0.8)',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                }}
                role="dialog"
                aria-label="Session details"
              >
                <p className="text-[10px] font-bold text-[#F97316] uppercase tracking-widest mb-2.5">Session Details</p>
                <div className="space-y-1.5">
                  {[
                    { label: 'Subject ID', value: subjectId },
                    { label: 'Session ID', value: sessionId },
                    { label: 'Protocol', value: 'Cognitive Fatigue v2.3' },
                    { label: 'Device', value: 'BrainAmp MR 64ch' },
                    { label: 'Amplifier', value: 'actiCHamp Plus' },
                    { label: 'Reference', value: 'Linked Mastoids (A1+A2)' },
                  ].map(item => (
                    <div key={item.label} className="flex items-center justify-between py-1 border-b border-[#1E3A5F]/20 last:border-0">
                      <span className="text-[11px] text-zinc-600">{item.label}</span>
                      <span className="text-[11px] font-mono text-zinc-300">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex-1" />

          {/* ── RIGHT ZONE: Status + Controls + User ── */}
          <div className="flex items-center gap-1.5 flex-shrink-0">

            {/* Cognitive Alert Bell */}
            <CognitiveAlertBell />

            {/* Sample rate — desktop only */}
            <div className="hidden xl:flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-zinc-600 flex-shrink-0">
              <Activity size={11} className="text-[#2D5A8E]" aria-hidden="true" />
              <span className="font-mono">{sampleRate} Hz</span>
            </div>

            {/* Duration — hidden on xs, visible sm+ */}
            <div
              className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border flex-shrink-0"
              style={{ background: 'rgba(11,22,40,0.8)', borderColor: 'rgba(30,58,95,0.5)', minHeight: '36px' }}
              aria-label={`Session duration: ${formatDuration(sessionDuration)}`}
            >
              <Clock size={12} className="text-[#2D5A8E]" aria-hidden="true" />
              <span className="text-[12px] font-mono tabular-nums text-zinc-300">{formatDuration(sessionDuration)}</span>
            </div>

            {/* Live status */}
            <div
              className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1.5 rounded-full border flex-shrink-0"
              style={{ background: 'rgba(11,22,40,0.8)', borderColor: 'rgba(30,58,95,0.5)', minHeight: '36px' }}
              role="status"
              aria-label={`Connection status: ${statusConfig.label}`}
            >
              {status === 'stopped'
                ? <WifiOff size={13} className="text-zinc-500" aria-hidden="true" />
                : <Wifi size={13} className={statusConfig.color} aria-hidden="true" />
              }
              <span className={`text-[11px] font-semibold ${statusConfig.color} hidden sm:inline`}>{statusConfig.label}</span>
              {statusConfig.pulse && (
                <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot} animate-pulse`} aria-hidden="true" />
              )}
            </div>

            <div className="w-px h-5 bg-[#1E3A5F] flex-shrink-0 hidden sm:block" aria-hidden="true" />

            {/* Session controls — hidden on mobile (accessible via mobile menu) */}
            {status !== 'stopped' && (
              <button
                onClick={togglePause}
                disabled={status === 'connecting'}
                title={status === 'paused' ? 'Resume streaming' : 'Pause streaming'}
                className="hidden sm:flex btn-secondary px-2.5 py-1.5 text-[12px] gap-1.5"
                style={{ minHeight: '36px' }}
                aria-label={status === 'paused' ? 'Resume streaming' : 'Pause streaming'}
              >
                {status === 'paused'
                  ? <><Play size={13} className="text-emerald-400" aria-hidden="true" /><span className="hidden md:inline">Resume</span></>
                  : <><Pause size={13} className="text-[#F97316]" aria-hidden="true" /><span className="hidden md:inline">Pause</span></>
                }
              </button>
            )}

            {status !== 'stopped' && (
              <button
                onClick={handleStop}
                title="Stop session"
                className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[12px] font-semibold bg-red-950/30 border border-red-800/40 hover:bg-red-900/40 hover:border-red-600/60 text-red-400 hover:text-red-300 transition-all duration-150 active:scale-95 touch-manipulation"
                style={{ minHeight: '36px' }}
                aria-label="Stop session"
              >
                <Square size={12} fill="currentColor" aria-hidden="true" />
                <span className="hidden md:inline">Stop</span>
              </button>
            )}

            {status === 'stopped' && (
              <button
                onClick={restartSession}
                title="Restart session"
                className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[12px] font-semibold bg-[#F97316]/10 border border-[#F97316]/30 hover:bg-[#F97316]/20 hover:border-[#F97316]/60 text-[#F97316] transition-all duration-150 active:scale-95 touch-manipulation"
                style={{ minHeight: '36px' }}
                aria-label="Restart session"
              >
                <RotateCcw size={13} aria-hidden="true" />
                <span className="hidden md:inline">Restart</span>
              </button>
            )}

            <button
              onClick={handleExport}
              title="Export session data"
              className="hidden sm:flex items-center justify-center w-9 h-9 rounded-lg border transition-all duration-150 active:scale-95 touch-manipulation"
              style={{ background: 'rgba(11,22,40,0.8)', borderColor: 'rgba(30,58,95,0.5)', color: '#2D5A8E' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(249,115,22,0.4)'; (e.currentTarget as HTMLElement).style.color = '#F97316'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(30,58,95,0.5)'; (e.currentTarget as HTMLElement).style.color = '#2D5A8E'; }}
              aria-label="Export session data"
            >
              <Download size={14} aria-hidden="true" />
            </button>

            {/* User menu — desktop */}
            {profile ? (
              <div ref={userRef} className="relative flex-shrink-0 hidden sm:block">
                <button
                  onClick={() => setShowUserMenu(v => !v)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border transition-all duration-150 active:scale-95 touch-manipulation"
                  style={{
                    background: showUserMenu ? 'rgba(15,30,53,0.9)' : 'rgba(11,22,40,0.8)',
                    borderColor: showUserMenu ? 'rgba(249,115,22,0.4)' : 'rgba(30,58,95,0.5)',
                    minHeight: '36px',
                  }}
                  title="User menu"
                  aria-label="User menu"
                  aria-expanded={showUserMenu}
                >
                  <div className={`flex items-center justify-center w-5 h-5 rounded-full border text-[10px] ${roleBadgeClass}`} aria-hidden="true">
                    {roleIcon}
                  </div>
                  <span className="text-[12px] font-medium text-zinc-300 hidden md:inline max-w-[72px] truncate">
                    {profile.fullName?.split(' ')[0]}
                  </span>
                  <ChevronDown size={11} className={`text-[#2D5A8E] transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`} aria-hidden="true" />
                </button>

                {showUserMenu && (
                  <div
                    className="absolute top-full right-0 mt-2 w-52 rounded-xl border z-50 p-2 animate-scale-in"
                    style={{
                      background: 'rgba(8,16,30,0.98)',
                      borderColor: 'rgba(30,58,95,0.8)',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
                    }}
                    role="dialog"
                    aria-label="User menu"
                  >
                    <div className="px-3 py-2 mb-1 border-b border-[#1E3A5F]/40">
                      <p className="text-xs font-semibold text-white truncate">{profile.fullName}</p>
                      <p className="text-[11px] text-zinc-500 truncate">{profile.email}</p>
                      {roleLabel && (
                        <span className={`inline-flex items-center gap-1 mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${roleBadgeClass}`}>
                          {roleIcon} {roleLabel}
                        </span>
                      )}
                    </div>
                    <Link
                      href={roleDashboardHref}
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-zinc-300 hover:text-white hover:bg-[#0F1E35] transition-colors touch-manipulation"
                      style={{ minHeight: '36px' }}
                    >
                      <User size={13} className="text-[#2D5A8E]" aria-hidden="true" />
                      My Dashboard
                    </Link>
                    {(role === 'admin') && (
                      <Link
                        href="/api-validation-report"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-zinc-300 hover:text-white hover:bg-[#0F1E35] transition-colors touch-manipulation"
                        style={{ minHeight: '36px' }}
                      >
                        <Settings size={13} className="text-[#2D5A8E]" aria-hidden="true" />
                        Connections & API
                      </Link>
                    )}
                    <button
                      onClick={() => { setShowUserMenu(false); handleSignOut(); }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-zinc-400 hover:text-red-400 hover:bg-red-950/20 transition-colors touch-manipulation"
                      style={{ minHeight: '36px' }}
                    >
                      <LogOut size={13} aria-hidden="true" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                href="/login"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-[#F97316]/10 border border-[#F97316]/30 hover:bg-[#F97316]/20 text-[#F97316] transition-all duration-150 touch-manipulation"
                style={{ minHeight: '36px' }}
              >
                <User size={13} aria-hidden="true" />
                Sign in
              </Link>
            )}

            {/* Current time — xl+ only */}
            <div className="hidden xl:flex items-center text-[12px] font-mono text-[#2D5A8E] tabular-nums ml-0.5 flex-shrink-0" aria-label={`Current time: ${currentTime}`}>
              {currentTime}
            </div>

            {/* ── Mobile hamburger — visible on xs/sm only ── */}
            <button
              onClick={() => setShowMobileMenu(v => !v)}
              className="sm:hidden flex items-center justify-center w-10 h-10 rounded-xl border transition-all duration-150 active:scale-95 touch-manipulation"
              style={{
                background: showMobileMenu ? 'rgba(249,115,22,0.1)' : 'rgba(11,22,40,0.8)',
                borderColor: showMobileMenu ? 'rgba(249,115,22,0.4)' : 'rgba(30,58,95,0.5)',
                color: showMobileMenu ? '#F97316' : '#8BAFD4',
              }}
              aria-label={showMobileMenu ? 'Close menu' : 'Open menu'}
              aria-expanded={showMobileMenu}
            >
              {showMobileMenu ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      {/* ── Mobile slide-down menu ── */}
      {showMobileMenu && (
        <div
          className="sm:hidden fixed inset-0 z-40"
          onClick={() => setShowMobileMenu(false)}
        >
          <div
            className="absolute top-[57px] left-0 right-0 border-b border-[#1E3A5F]/80 overflow-y-auto"
            style={{
              background: 'rgba(6,13,26,0.99)',
              backdropFilter: 'blur(20px)',
              maxHeight: 'calc(100vh - 57px)',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Session controls row */}
            <div className="px-4 py-3 border-b border-[#1E3A5F]/40 flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 text-[11px] text-zinc-400">
                <Clock size={11} className="text-[#2D5A8E]" />
                <span className="font-mono tabular-nums">{formatDuration(sessionDuration)}</span>
              </div>
              <div className="flex items-center gap-1.5 text-[11px]">
                {status === 'stopped'
                  ? <WifiOff size={12} className="text-zinc-500" />
                  : <Wifi size={12} className={statusConfig.color} />
                }
                <span className={`font-semibold ${statusConfig.color}`}>{statusConfig.label}</span>
              </div>
              <div className="flex-1" />
              {status !== 'stopped' && (
                <button
                  onClick={() => { togglePause(); setShowMobileMenu(false); }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-semibold border touch-manipulation active:scale-95"
                  style={{ background: 'rgba(11,22,40,0.8)', borderColor: 'rgba(30,58,95,0.5)', color: '#8BAFD4', minHeight: '44px' }}
                >
                  {status === 'paused' ? <><Play size={13} className="text-emerald-400" />Resume</> : <><Pause size={13} className="text-[#F97316]" />Pause</>}
                </button>
              )}
              {status !== 'stopped' && (
                <button
                  onClick={() => { handleStop(); setShowMobileMenu(false); }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-semibold bg-red-950/30 border border-red-800/40 text-red-400 touch-manipulation active:scale-95"
                  style={{ minHeight: '44px' }}
                >
                  <Square size={12} fill="currentColor" />Stop
                </button>
              )}
              {status === 'stopped' && (
                <button
                  onClick={() => { restartSession(); setShowMobileMenu(false); }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-semibold bg-[#F97316]/10 border border-[#F97316]/30 text-[#F97316] touch-manipulation active:scale-95"
                  style={{ minHeight: '44px' }}
                >
                  <RotateCcw size={13} />Restart
                </button>
              )}
              <button
                onClick={() => { handleExport(); setShowMobileMenu(false); }}
                className="flex items-center justify-center w-10 h-10 rounded-lg border touch-manipulation active:scale-95"
                style={{ background: 'rgba(11,22,40,0.8)', borderColor: 'rgba(30,58,95,0.5)', color: '#2D5A8E', minHeight: '44px' }}
                aria-label="Export"
              >
                <Download size={14} />
              </button>
            </div>

            {/* Nav groups */}
            <div className="px-4 py-3 space-y-4">
              {filteredGroups.map(group => (
                <div key={group.label}>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-1.5">{group.label}</p>
                  <div className="space-y-1">
                    {group.items.map(item => {
                      const isActive = pathname === item.href;
                      return (
                        <Link
                          key={item.href}
                          href={item.href}
                          onClick={() => setShowMobileMenu(false)}
                          className={`flex items-center gap-3 px-3 py-3 rounded-xl text-[13px] font-medium transition-all touch-manipulation active:scale-[0.98] ${
                            isActive
                              ? 'bg-[#F97316]/10 text-[#F97316] border border-[#F97316]/20'
                              : 'text-zinc-400 hover:text-white bg-[#0B1628]/60 border border-[#1E3A5F]/30'
                          }`}
                          style={{ minHeight: '48px' }}
                        >
                          <span className={`flex-shrink-0 ${item.accent ?? (isActive ? 'text-[#F97316]' : 'text-[#2D5A8E]')}`}>{item.icon}</span>
                          <span className="flex-1">{item.label}</span>
                          {isActive && <span className="w-2 h-2 rounded-full bg-[#F97316] flex-shrink-0" />}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* PPG quick access */}
              {role !== 'patient' && (
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 mb-1.5">Quick Access</p>
                  <button
                    onClick={() => {
                      setShowMobileMenu(false);
                      const el = document.querySelector('[data-section="ppg"]');
                      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    className="flex items-center gap-3 px-3 py-3 rounded-xl text-[13px] font-medium text-zinc-400 bg-[#0B1628]/60 border border-[#1E3A5F]/30 touch-manipulation active:scale-[0.98] w-full text-left"
                    style={{ minHeight: '48px' }}
                  >
                    <Heart size={14} className="text-[#2D5A8E]" />
                    PPG Heart Rate Monitor
                    <ChevronRight size={12} className="ml-auto text-zinc-600" />
                  </button>
                </div>
              )}
            </div>

            {/* User section */}
            {profile && (
              <div className="px-4 py-3 border-t border-[#1E3A5F]/40 space-y-1">
                <div className="px-3 py-2 mb-1">
                  <p className="text-xs font-semibold text-white">{profile.fullName}</p>
                  <p className="text-[11px] text-zinc-500">{profile.email}</p>
                  {roleLabel && (
                    <span className={`inline-flex items-center gap-1 mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${roleBadgeClass}`}>
                      {roleIcon} {roleLabel}
                    </span>
                  )}
                </div>
                <Link
                  href={roleDashboardHref}
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl text-[13px] text-zinc-300 hover:text-white bg-[#0B1628]/60 border border-[#1E3A5F]/30 touch-manipulation"
                  style={{ minHeight: '48px' }}
                >
                  <User size={14} className="text-[#2D5A8E]" />
                  My Dashboard
                </Link>
                {role === 'admin' && (
                  <Link
                    href="/api-validation-report"
                    onClick={() => setShowMobileMenu(false)}
                    className="flex items-center gap-3 px-3 py-3 rounded-xl text-[13px] text-zinc-300 hover:text-white bg-[#0B1628]/60 border border-[#1E3A5F]/30 touch-manipulation"
                    style={{ minHeight: '48px' }}
                  >
                    <Settings size={14} className="text-[#2D5A8E]" />
                    Connections & API
                  </Link>
                )}
                <button
                  onClick={() => { setShowMobileMenu(false); handleSignOut(); }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-[13px] text-zinc-400 hover:text-red-400 bg-[#0B1628]/60 border border-[#1E3A5F]/30 touch-manipulation"
                  style={{ minHeight: '48px' }}
                >
                  <LogOut size={14} />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}